// mvp-returns Phase E — DamagedInventory client matching the
// /ord/damaged-inventory/* endpoints added in backend Phase D3.

import type { InventoryImpactType, IReturnDamagedInventory } from "./return";
import { HttpRequest } from "../utils";

const httpRequest = new HttpRequest();

export interface IDamagedInventoryListParams {
  inventoryImpact?: InventoryImpactType | InventoryImpactType[];
  returnRequestId?: string;
  productId?: string;
  sku?: string;
  warehouseLocationId?: string;
  disposed?: boolean;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}

export interface IDamagedInventoryListResponse {
  items: IReturnDamagedInventory[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export type FinalDisposition = "DESTROYED" | "RESOLD" | "DONATED" | "RETURNED_TO_SUPPLIER";

export const list = (params?: IDamagedInventoryListParams) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.get("/ord/damaged-inventory", params as any) as Promise<IDamagedInventoryListResponse>;

export const get = (id: string) =>
  httpRequest.get(`/ord/damaged-inventory/${id}`) as Promise<IReturnDamagedInventory>;

export const dispose = (id: string, body: {
  finalDisposition: FinalDisposition;
  disposalNotes?: string | null;
}) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/damaged-inventory/${id}/dispose`, body as any);

// ─── Turnover analytics (Phase H11) ──────────────────────────────────────────

export interface IDamagedTurnoverBucket {
  inventoryImpact : string;
  activeRows      : number;
  activeQuantity  : number;
  avgDaysInBucket : number;
  disposedRows    : number;
  disposedQuantity: number;
  avgDaysToDispose: number;
}

export interface IDamagedTurnoverSlowMover {
  id             : string;
  productName    : string | null;
  sku            : string | null;
  productId      : string | null;
  inventoryImpact: string;
  quantity       : number;
  daysInBucket   : number;
  receivedAt     : string;
  returnRequestId: string;
  returnNumber   : string | null;
}

export interface IDamagedTurnoverResponse {
  // inventoryImpacts: the damaged categories the slow-movers list was built from (never RESTOCK_GOOD).
  threshold  : { days: number; limit: number; inventoryImpacts?: string[] };
  buckets    : IDamagedTurnoverBucket[];
  slowMovers : IDamagedTurnoverSlowMover[];
}

// `buckets` always holds the four categories in a fixed order; `inventoryImpact` narrows slow movers to one damaged category.
export const turnover = (params?: { slowMoverThresholdDays?: number; slowMoverLimit?: number; inventoryImpact?: Exclude<InventoryImpactType, "RESTOCK_GOOD"> }) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.get("/ord/damaged-inventory/turnover", (params ?? {}) as any) as Promise<IDamagedTurnoverResponse>;

// ─── Bulk actions (Phase E3 — mvp-returns §9.5) ──────────────────────────────

export interface IBulkResultItem {
  id          : string;
  ok          : boolean;
  errorCode   ?: string;
  errorMessage?: string;
}

export interface IBulkResponse {
  total    : number;
  succeeded: number;
  failed   : number;
  results  : IBulkResultItem[];
}

export const bulkDispose = (input: {
  damagedInventoryIds: string[];
  finalDisposition   : FinalDisposition;
  disposalNotes?     : string | null;
}) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post("/ord/damaged-inventory/bulk-dispose", input as any) as Promise<IBulkResponse>;

export const moveBucket = (id: string, input: {
  inventoryImpact: InventoryImpactType;
  notes?         : string | null;
}) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/damaged-inventory/${id}/move-bucket`, input as any);

export const bulkMoveBucket = (input: {
  damagedInventoryIds: string[];
  inventoryImpact    : InventoryImpactType;
  notes?             : string | null;
}) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post("/ord/damaged-inventory/bulk-move", input as any) as Promise<IBulkResponse>;
